import { AxiosPromise } from 'axios';

import request from '../utils/request';
import {
  AuthenticationInfoForSpodMigrationRes,
  AuthenticationInfoRes,
  EnabledPolicyTypeEnum,
  IdSyncMode,
  MigrationStatusForSpodMigrationRes,
  MultiAppsAuthenticationInfoRes,
  PaymentModel,
  ProvisionStatus,
  ReauthRequiredOrgsResponse,
  ServicesStatusListRes,
  TargetType,
} from '@Types/provision';
import { AppForSa, ServiceForReAuth } from '@Types/administrator';
import { App, Service } from '@Types/app';
import { MigrationService } from '../pages/Administrator/ServiceAccount/types';
import { ProvisionEntity } from '@Types/provision-entity';

function getProvisionedServices(orgId: string): AxiosPromise<number[]> {
  const url =
    orgId === 'all'
      ? 'company/provisioned/serviceTypes'
      : 'org/provisioned/serviceTypes';
  return request({
    url,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function getProvisionedServicesByOrgId(orgId: string): AxiosPromise<number[]> {
  const url =
    orgId === 'all'
      ? 'company/provisioned/serviceTypes'
      : `org/provisioned/serviceTypes`;
  return request({
    url,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function apiGetServicesStatusList(): AxiosPromise<ServicesStatusListRes> {
  return request({
    url: 'services/status',
    method: 'get',
  });
}

function apiGetProvisionStatus(
  service: ProvisionEntity,
  orgId: string
): AxiosPromise<ProvisionStatus> {
  return request({
    url: `apps/${service}/provision/status`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function apiGetAuthenticationInfo(
  service: ProvisionEntity,
  orgId: string
): AxiosPromise<AuthenticationInfoRes> {
  return request({
    url: `apps/${service}/provision/authentications`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function apiGetMultiAppsAuthenticationInfos(
  service:
    | Service.ExchangeInline
    | Service.Teams
    | Service.OneDrive
    | Service.SharePoint,
  orgId: string
): AxiosPromise<MultiAppsAuthenticationInfoRes> {
  return request({
    url: `apps/${service}/provision/authentications`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function apiGetReAuthAuthenticationInfo(
  service: Exclude<ServiceForReAuth, ServiceForReAuth.ExchangeInline>,
  orgId: string
): AxiosPromise<AuthenticationInfoRes> {
  return request({
    url: `apps/${service}/reAuth/authentications`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function apiGetMultiAppsReAuthAuthenticationInfos(
  service: ProvisionEntity,
  orgId: string
): AxiosPromise<MultiAppsAuthenticationInfoRes> {
  return request({
    url: `apps/${service}/reAuth/authentications`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function apiGetTeamsChatRenewAuthentications(
  orgId: string
): AxiosPromise<AuthenticationInfoRes> {
  return request({
    url: `apps/${Service.TeamsChat}/renew/authentications`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

// {
//   "traceId": "3e18dd09-b891-4a11-9492-718b928030f2",
//   "data": [
//       {
//           "app": "exchangeInlineMailFlow",
//           "installAppUrl": null,
//           "authenticationUrl": "https://login.microsoftonline.com/common/oauth2/authorize?client_id=8af0038e-67da-438d-9424-ce0e1e5f6325&response_type=code&prompt=consent&resource=https://graph.microsoft.com&state=azuread_mail_flow_inline_ex_72251c12c07a15cdcea1993e218c9620&redirect_uri=https%3A%2F%2Fadmin-dev.tmcas.trendmicro.net%2Fprovision.html",
//           "appId": "8af0038e-67da-438d-9424-ce0e1e5f6325"
//       },
//       {
//           "app": "exchangeInline",
//           "installAppUrl": null,
//           "authenticationUrl": "https://login.microsoftonline.com/common/oauth2/authorize?client_id=f362d789-5a05-4faa-9708-70933c56e3ab&response_type=code&prompt=consent&resource=https://graph.microsoft.com&state=azuread_mail_inline_graph_api_72251c12c07a15cdcea1993e218c9620&redirect_uri=https%3A%2F%2Fadmin-dev.tmcas.trendmicro.net%2Fprovision.html",
//           "appId": "f362d789-5a05-4faa-9708-70933c56e3ab"
//       }
//   ]
// }

function apiSaveProvisionInfoForTeamsChat(
  code: string,
  model: PaymentModel,
  appId: string,
  appSecret: string,
  enabledPolicyType: EnabledPolicyTypeEnum,
  orgId: string
) {
  const enabledPolicy = new Map<EnabledPolicyTypeEnum, string>([
    [EnabledPolicyTypeEnum.Default, 'default'],
    [EnabledPolicyTypeEnum.DefaultMonitor, 'defaultMonitor'],
    [EnabledPolicyTypeEnum.None, 'none'],
  ]).get(enabledPolicyType);

  return request({
    url: `apps/teamsChat/provision/initToken`,
    method: 'post',
    data: {
      authCode: code,
      policy: {
        enabledPolicy,
      },
      paymentModel: model,
      userApp: {
        customerAppId: appId,
        customerAppSecret: appSecret,
      },
    },
    headers: { 'x-org-id': orgId },
  });
}

function apiRenewTeamsChat(
  code: string,
  model: PaymentModel,
  appId: string,
  appSecret: string,
  orgId: string
) {
  return request({
    url: `apps/teamsChat/renew`,
    method: 'post',
    data: {
      authCode: code,
      authAccount: 'none',
      paymentModel: model,
      userApp: {
        customerAppId: appId,
        customerAppSecret: appSecret,
      },
    },
    headers: { 'x-org-id': orgId },
  });
}

function apiSaveProvisionInfo(
  app: App,
  code: string,
  enabledPolicyType: EnabledPolicyTypeEnum,
  orgId: string,
  isPoc = false
) {
  //TODO: need enhancement
  const enabledPolicy = new Map<EnabledPolicyTypeEnum, string>([
    [EnabledPolicyTypeEnum.Default, 'default'],
    [EnabledPolicyTypeEnum.DefaultMonitor, 'defaultMonitor'],
    [EnabledPolicyTypeEnum.None, 'none'],
  ]).get(enabledPolicyType);

  const data = {
    authCode: code,
    // policy config
    policy: {
      //policySubType: 0 | 1   outbound or inbound
      //policyHolderType:  dlp | atp
      enabledPolicy,
    },
    // just for exchange online
    ...(isPoc ? { isPoc: true } : {}),
  };

  return request({
    url: `apps/${app}/provision/initToken`,
    method: 'post',
    data,
    headers: { 'x-org-id': orgId },
  });
}

function apiSaveProvisionInfoForStorage(
  service:
    | Service.Box
    | Service.Dropbox
    | Service.GoogleDrive
    | Service.SalesforceProduction
    | Service.SalesforceSandbox,
  code: {
    authCode: string;
    csrfCode: string;
  },
  enabledPolicyType: EnabledPolicyTypeEnum,
  orgId: string
) {
  //TODO: need enhancement
  const enabledPolicy = new Map<EnabledPolicyTypeEnum, string>([
    [EnabledPolicyTypeEnum.Default, 'default'],
    [EnabledPolicyTypeEnum.DefaultMonitor, 'defaultMonitor'],
    [EnabledPolicyTypeEnum.None, 'none'],
  ]).get(enabledPolicyType);
  return request({
    url: `apps/${service}/provision/initToken`,
    method: 'post',
    data: {
      ...code,
      policy: {
        enabledPolicy,
      },
    },
    headers: { 'x-org-id': orgId },
  });
}

function apiSaveReAuthInfo(
  service:
    | ServiceForReAuth
    | App.ExchangeInlineMailFlow
    | App.Teams
    | App.TeamsBasic
    | App.SharePoint
    | App.SharePointBasic
    | App.OneDrive
    | App.OneDriveBasic,
  code: string,
  authAccount: string,
  orgId: string
) {
  return request({
    url: `apps/${service}/reAuth`,
    method: 'post',
    data: {
      authCode: code,
      authAccount,
    },
    headers: { 'x-org-id': orgId },
  });
}

function apiSaveReAuthInfoForStorage(
  service:
    | ServiceForReAuth.Box
    | ServiceForReAuth.Dropbox
    | ServiceForReAuth.GoogleDrive,
  code: {
    authCode: string;
    csrfCode: string;
  },
  authAccount: string,
  orgId: string,
  useNewServiceAccount?: boolean
) {
  return request({
    url: `apps/${service}/reAuth`,
    method: 'post',
    data: {
      ...code,
      authAccount,
      useNewServiceAccount,
    },
    headers: { 'x-org-id': orgId },
  });
}

function apiGetGmailInlineSetting(orgId: string): AxiosPromise<{
  inboundHostname: string;
  outboundHostname: string;
  outboundLoopHeader: string;
  inboundLoopHeader: string;
}> {
  return request({
    url: `org/gmailInline/protectionInfo`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function apiCheckExchangeAdminRole(orgId: string): AxiosPromise<boolean> {
  return request({
    url: 'apps/exchangeInline/checkRole',
    method: 'post',
    headers: { 'x-org-id': orgId },
  });
}

function apiRetrySyncData(service: string, orgId: string) {
  return request({
    url: `services/${service}/targets/sync`,
    method: 'post',
    data: {
      syncMode: IdSyncMode.Full,
      targetType: TargetType.User,
    },
    headers: { 'x-org-id': orgId },
  });
}

// function getAppToken(orgId: string, app: App) {
//   return request({
//     url: `orgs/${orgId}/apps/${app}/tokens`,
//     method: 'get',
//   });
// }

function apiDeprovision(orgId: string, app: AppForSa) {
  return request({
    url: `/apps/${app}/deProvision`,
    method: 'post',
    headers: { 'x-org-id': orgId },
  });
}

function apiForceDeprovision(orgId: string, app: AppForSa) {
  return request({
    url: `/apps/${app}/deProvision`,
    method: 'post',
    headers: { 'x-org-id': orgId },
    data: {
      deProvisionType: 'force',
    },
  });
}

// function getAppRealtimeScanSupport(app: App) {
//   return request({
//     method: 'get',
//     url: `/apps/${app}/realtimeScanSupport`,
//   });
// }
////////////////////////////// Spod migration start //////////////////////////////////
function apiGetSpodMigrationAuthenticationInfo(
  orgId: string
): AxiosPromise<AuthenticationInfoForSpodMigrationRes> {
  return request({
    url: `apps/deprecatedSpod/migration/authentications`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function apiGetSpodMigrationStatus(
  orgId: string
): AxiosPromise<MigrationStatusForSpodMigrationRes> {
  return request({
    url: `apps/deprecatedSpod/migration/status`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function apiSaveSpodMigrationInfo(
  app: MigrationService.SharePoint | MigrationService.OneDrive,
  code: string,
  orgId: string
): AxiosPromise<any> {
  return request({
    url: `apps/${app}/migration`,
    method: 'post',
    data: {
      authCode: code,
    },
    headers: { 'x-org-id': orgId },
  });
}

function apiSubmitSpodMigration(orgId: string) {
  return request({
    url: 'apps/deprecatedSpod/targets/sync',
    method: 'post',
    data: {
      syncMode: 'migration',
      targetType: 'user',
    },
    headers: { 'x-org-id': orgId },
  });
}

function apiVerifiedSpodAccount(
  email: string,
  pwd: string,
  orgId: string
): AxiosPromise<{
  status: string;
}> {
  return request({
    url: 'apps/deprecatedSpod/accounts/verify',
    method: 'post',
    data: {
      adminEmail: email,
      adminPwd: pwd,
    },
    headers: { 'x-org-id': orgId },
  });
}

function apiCleanUpSpodAccount(
  email: string,
  pwd: string,
  orgId: string
): AxiosPromise<{
  status: string;
}> {
  return request({
    url: 'apps/deprecatedSpod/accounts/cleanup',
    method: 'post',
    data: {
      adminEmail: email,
      adminPwd: pwd,
    },
    headers: { 'x-org-id': orgId },
  });
}

function getMigrationRequirement(
  orgId: string,
  app: App.SharePoint | App.OneDrive
) {
  return request({
    url: `apps/${app}/deprecatedSpod/migration/requirement`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function getReauthRequiredOrgs(): Promise<ReauthRequiredOrgsResponse> {
  return request({
    url: `company/reauthRequiredOrgs`,
    method: 'get',
    headers: { 'x-org-id': 'all' },
  });
}

////////////////////////////// Spod migration end //////////////////////////////////

export default {
  getProvisionedServices,
  getProvisionedServicesByOrgId,
  apiGetServicesStatusList,
  apiGetProvisionStatus,
  apiGetAuthenticationInfo,
  apiGetMultiAppsAuthenticationInfos,
  apiGetReAuthAuthenticationInfo,
  apiGetMultiAppsReAuthAuthenticationInfos,
  apiGetTeamsChatRenewAuthentications,
  apiSaveProvisionInfoForTeamsChat,
  apiRenewTeamsChat,
  apiSaveProvisionInfo,
  apiSaveProvisionInfoForStorage,
  apiSaveReAuthInfo,
  apiSaveReAuthInfoForStorage,
  apiGetGmailInlineSetting,
  apiCheckExchangeAdminRole,
  apiRetrySyncData,
  apiDeprovision,
  apiForceDeprovision,
  apiGetSpodMigrationAuthenticationInfo,
  apiGetSpodMigrationStatus,
  apiSaveSpodMigrationInfo,
  apiSubmitSpodMigration,
  apiVerifiedSpodAccount,
  apiCleanUpSpodAccount,
  getMigrationRequirement,
  getReauthRequiredOrgs,
};
